"use client";

import React, { useState } from 'react';
import { topVacancies } from '@/data/dummy';
import JobCard from '@/components/JobCard';
import styles from './page.module.css';
import { slugify } from '@/utils/slugify';
import Link from 'next/link';
import SubscribeButton from '@/components/SubscribeButton';
import AdminEditCompanyButton from '@/components/AdminEditCompanyButton';
import { useSubscribedCompanies } from '@/context/SubscribedCompaniesContext';
import { useLanguage } from '@/context/LanguageContext';
import ProtectedEmail from '@/components/ProtectedEmail';

const formatDisplayDate = (dateStr: string, lang: string) => {
  if (!dateStr) return '-';
  let date: Date;
  
  if (dateStr.includes('.')) {
    const parts = dateStr.split('.');
    if (parts.length === 3) {
      date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    } else {
      date = new Date(dateStr);
    }
  } else if (dateStr.includes('-') && dateStr.split('-')[0].length === 2) {
    const parts = dateStr.split('-');
    date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
  } else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return dateStr;
  
  const day = date.getDate();
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  
  const monthsEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthsSq = ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];
  
  const monthStr = lang === 'en' ? monthsEn[monthIndex] : monthsSq[monthIndex];
  return `${day} ${monthStr} ${year}`;
};

export default function SingleCompanyClient({ company, companySlug, companyJobs = [] }: { company: any; companySlug: string; companyJobs?: any[] }) {
  const { language, t } = useLanguage();
  const lang = language || 'sq';
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const { isSubscribed } = useSubscribedCompanies();

  const getYoutubeEmbedUrl = (url: string) => {
    if (!url) return null;
    let videoId = '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      videoId = match[2];
    } else if (url.length === 11) {
      videoId = url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  if (!company) {
    return (
      <div className={styles.container}>
        <div style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <h2>Company not found</h2>
          <Link href={`/${lang}/companies`} style={{ color: '#1c7bff', marginTop: '1rem', display: 'inline-block' }}>
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.topSection}>
        <div className={styles.container}>
          <div className={styles.companyHeader}>
            {company.logo ? (
              <img src={company.logo} alt={company.name} className={styles.companyLogo} />
            ) : (
              <div className={styles.companyLogoPlaceholder}>{company.name.charAt(0)}</div>
            )}
            
            <div className={styles.companyInfo}>
              <h1>{company.name}</h1>
              <div className={styles.metaList}>
                {company.location && (
                  <div className={styles.metaItem}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                    <span>{company.location}</span>
                  </div>
                )}
                {company.category && (
                  <div className={styles.metaItem}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                    <span>{company.category}</span>
                  </div>
                )}
                <div className={styles.metaItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  <span>{company.employeesCount ? `${company.employeesCount} Employees` : '-'}</span>
                </div>
              </div>
            </div>

            <div className={styles.headerActions}>
              <AdminEditCompanyButton companyId={company.id} />
              <SubscribeButton 
                company={{
                  name: company.name,
                  logo: company.logo,
                  location: company.location
                }}
                className={styles.subscribeBtn}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.contentGrid}>
          <div className={styles.mainContent}>
            <h2>About Company</h2>
            <div 
              className={styles.aboutText} 
              dangerouslySetInnerHTML={{ __html: company.description || company.about || '<p>No description provided.</p>' }} 
            />

            {company.gallery && company.gallery.length > 0 && (
              <div style={{ marginTop: '2.5rem' }}>
                <h2>Photo Gallery</h2>
                <div className={styles.galleryGrid}>
                  {company.gallery.map((img: string, i: number) => (
                    <div key={i} className={styles.galleryImageContainer} onClick={() => setSelectedImageIndex(i)}>
                      <img src={img} alt={`${company.name} gallery ${i+1}`} />
                      <div className={styles.galleryImageOverlay}>
                        <div className={styles.galleryPlusIcon}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {company.youtubeVideo && getYoutubeEmbedUrl(company.youtubeVideo) && (
              <div style={{ marginTop: '2.5rem' }}>
                <h2>Youtube Video</h2>
                <div style={{ marginTop: '1.5rem' }}>
                  <iframe 
                    width="100%" 
                    height="400" 
                    src={getYoutubeEmbedUrl(company.youtubeVideo)!} 
                    title="Company Video" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    style={{ borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  ></iframe>
                </div>
              </div>
            )}
          </div>
          
          <div>
            <div className={styles.metaCard}>
              <div className={styles.metaCardRow}>
                <span className={styles.metaCardLabel}>Primary industry:</span>
                <span className={styles.metaCardValue}>{company.category || '-'}</span>
              </div>
              <div className={styles.metaCardRow}>
                <span className={styles.metaCardLabel}>Company size:</span>
                <span className={styles.metaCardValue}>{company.employeesCount ? `${company.employeesCount} Employees` : '-'}</span>
              </div>
              <div className={styles.metaCardRow}>
                <span className={styles.metaCardLabel}>Founded in:</span>
                <span className={styles.metaCardValue}>{formatDisplayDate(company.foundedIn, lang)}</span>
              </div>
              {company.phone && (
                <div className={styles.metaCardRow}>
                  <span className={styles.metaCardLabel}>Phone:</span>
                  <span className={styles.metaCardValue}>
                    {company.phone.startsWith('+') ? company.phone : `+${company.phone}`}
                  </span>
                </div>
              )}
              <div className={styles.metaCardRow}>
                <span className={styles.metaCardLabel}>Email:</span>
                <span className={styles.metaCardValue}>{company.email ? <ProtectedEmail email={company.email} /> : '-'}</span>
              </div>
              <div className={styles.metaCardRow}>
                <span className={styles.metaCardLabel}>Location:</span>
                <span className={styles.metaCardValue}>{company.location || '-'}</span>
              </div>
              
              {(company.facebookUrl || company.twitterUrl || company.instagramUrl || company.linkedinUrl) && (
                <div className={styles.metaCardRow} style={{ marginTop: '1.5rem', marginBottom: 0, borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                  <span className={styles.metaCardLabel}>Social media:</span>
                  <div className={styles.socialRow}>
                    {company.facebookUrl && (
                      <a href={company.facebookUrl} target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                      </a>
                    )}
                    {company.twitterUrl && (
                      <a href={company.twitterUrl} target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>
                      </a>
                    )}
                    {company.instagramUrl && (
                      <a href={company.instagramUrl} target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                      </a>
                    )}
                    {company.linkedinUrl && (
                      <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {company.url && (
                <a href={company.url} target="_blank" rel="noopener noreferrer" className={styles.websiteBtn}>
                  {company.url.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
        </div>

        <div className={styles.jobsSection}>
          <h2 className={styles.jobsTitle}>
            {companyJobs.length === 1 ? t('Open vacancy') : t('Open vacancies')} ({companyJobs.length})
          </h2>
          
          {companyJobs.length > 0 ? (
            <div className={styles.jobsGrid}>
              {companyJobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          ) : (
            <div className={styles.noJobs}>No open vacancies at the moment.</div>
          )}
        </div>
      </div>
      {selectedImageIndex !== null && company.gallery && (
        <div className={styles.lightboxOverlay} onClick={() => setSelectedImageIndex(null)}>
          <img src={company.gallery[selectedImageIndex]} alt="Gallery Enlarge" className={styles.lightboxImage} onClick={(e) => e.stopPropagation()} />
          <button 
            onClick={() => setSelectedImageIndex(null)} 
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', zIndex: 10000 }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>

          {selectedImageIndex > 0 && (
            <button 
              className={styles.lightboxNavButton}
              style={{ left: '20px' }}
              onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(selectedImageIndex - 1); }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </button>
          )}

          {selectedImageIndex < company.gallery.length - 1 && (
            <button 
              className={styles.lightboxNavButton}
              style={{ right: '20px' }}
              onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(selectedImageIndex + 1); }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
